/* tslint:disable:max-file-line-count */

// Vendor
import * as React from 'react';
import { inject } from 'mobx-react';
import { Link } from 'react-router-dom';

// Bernie
import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, ExperimentStore, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { Context } from 'bernie-context';
import { ErrorBoundary, ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';
import { LocalizedText } from 'bernie-l10n';

// Lodging Stores
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { SearchResultsStore } from 'stores/search-results-store';
import { ShortlistStore } from 'stores/shortlist-store';

// Models
import { DisclaimerViewModel, GalleryViewModel, HotelViewModel } from 'models';

// Utils
import { onClickScrollToTarget } from 'utils/smooth-scroll';

// UITK
import { UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkRating, UitkRatingIcon } from 'uitk-react-rating';
import { UitkCard, UitkCardContent, UitkCardImageLayout, UitkCardMedia } from 'uitk-react-cards';
import { UitkIcon } from 'uitk-react-icons';
import { UitkMoreInfoTrigger } from 'uitk-react-more-info-trigger';

// Common components
import { Messaging } from 'components/common/messaging';

// Internals
import { AdditionalInfo } from './internals/additional-info';
import { AlternateDates } from './internals/alternate-dates';
import { CardLink } from './internals/card-link';
import { Footer } from './internals/footer';
import { ListingDescription } from './internals/listing-description';
import { ListingErrorBoundary } from './internals/listing-error-boundary';
import { ListingName } from './internals/listing-name';
import { ListingTravelAd } from './internals/listing-travel-ad';
import { Neighborhood } from './internals/neighborhood';
import { HighightedPropertyDetails } from './internals/highlighted-property-details';
import { PlaceholderCard } from './internals/placeholder-card';
import { PriceAndSupportingMessagesContainer } from './internals/price-and-supporting-messages-container';
import { SearchResultsReviews } from './internals/search-results-reviews';
import { Title } from './internals/title';
import { TitleHeading } from './internals/title-heading';
import { UitkBadge, UitkBadgeTheme } from 'uitk-react-badge';
import { DisclaimerDialogBulletList } from 'components/common/disclaimer-dialog-bullet-list';

const LinkTracked = ClickTracker(Link);

export interface ListingComponentProps {
  analytics?: AnalyticsStore;
  breakfastIncluded?: string;
  className: string;
  context?: Context;
  currentHotel?: CurrentHotelStore;
  'data-stid'?: string;
  dataAttributes?: any;
  endDate?: string;
  index?: number;
  gallery?: GalleryViewModel;
  lazyLoadImg?: boolean;
  loading?: boolean;
  searchResults?: SearchResultsStore;
  stars?: string;
  startDate?: string;
  source?: string;
  model: HotelViewModel;
  replayId?: string;
  referralObject?: {
    navigation?: {
      linkName?: string;
      rfrr?: string;
    };
    favorite?: {
      linkName?: string;
      rfrr?: string;
    };
    unfavorite?: {
      linkName?: string;
      rfrr?: string;
    };
    close?: {
      linkName?: string;
      rfrr?: string;
    };
  };
  routeStore?: RouteStore;
  shortlist?: ShortlistStore;
  displayViewed?: boolean;
  experiment?: ExperimentStore;
  setResultRef?: (ref: HTMLElement, index: number) => void;
  defaultListingOnly?: boolean;
  disableGallery?: boolean;
}

interface ListingComponentState {
  alternateDatesExpanded: boolean;
}

@inject('routeStore', 'analytics', 'currentHotel')
class ListingComponent extends React.Component<ListingComponentProps, ListingComponentState> {
  constructor(props) {
    super(props);

    this.listingClickHandler = this.listingClickHandler.bind(this);
    this.setItemRef = this.setItemRef.bind(this);
    this.handleAdditionalInfoClick = this.handleAdditionalInfoClick.bind(this);
    this.routeStoreGoBack = this.routeStoreGoBack.bind(this);
  }

  private static alternateDatesInfo = {
    collapsedLinkName: 'HSR show alt dates',
    collapsedMsgKey: 'hotels.searchResults.alternateDates.more',
    collapsedRfrr: 'HOT:SR:ShowAltDates',
    expandedLinkName: 'HSR hide alt dates',
    expandedMsgKey: 'hotels.searchResults.alternateDates.less',
    expandedRfrr: 'HOT:SR:HideAltDates',
    headerTextKey: 'hotels.searchResults.alternateDates.try'
  };

  private handleAdditionalInfoClick() {
    onClickScrollToTarget({ scrollTo: 0 });
  }

  private setItemRef(ref) {
    const { index, setResultRef } = this.props;
    if (setResultRef && typeof setResultRef === 'function') {
      setResultRef(ref, index);
    }
  }

  private listingClickHandler(): void {
    const { currentHotel, model, searchResults } = this.props;
    const id = model.id;
    let hotel;
    if (searchResults && searchResults.hotels && searchResults.hotels.length > 0) {
      hotel = searchResults.hotels.find(h => h.id === id);
    }
    if (model && model.travelAd) {
      const travelAd = model.travelAd;
      if (travelAd.clickRedirectLinkoffTrackingUrl) {
        window.open(travelAd.clickRedirectLinkoffTrackingUrl);
      }
    }

    if (currentHotel && currentHotel.setSearchResultAsCurrentHotel && hotel) {
      currentHotel.setSearchResultAsCurrentHotel(hotel);

      return;
    }
  }

  private getTitleComponent(imageRatio = UitkFigureAspectRatioType.R21_9, extraProps = {}) {
    const { index, lazyLoadImg, model, referralObject, displayViewed, disableGallery } = this.props;
    const {
      id,
      propertyListingBadgeModel,
      infositeUrl,
      name,
      neighborhood,
      pinnedDetails,
      price,
      reviews,
      shortlistModel,
      starRatingIcon,
      stars,
      thumbnail,
      travelAd
    } = model;

    const { analytics, currentHotel } = this.props;
    let gallery = null;
    if (!disableGallery) {
      gallery = {
        images: model && model.gallery ? model.gallery.images : [],
        thumbnailGalleryEnabled: false
      };
    }
    const titleProps = {
      analytics,
      currentHotel,
      channel: pinnedDetails && pinnedDetails.channelName,
      className: 'listing__title',
      propertyListingBadgeModel,
      imageUrl: (thumbnail && thumbnail.src) || '',
      fallbackImageUrl: (thumbnail && thumbnail.fallBackImage) || '',
      gallery,
      index,
      lazyLoadImg,
      name,
      pinned: pinnedDetails,
      neighborhood,
      stars,
      starRatingIcon,
      travelAd,
      shortlistModel,
      replayId: this.props.replayId,
      infositeUrl,
      thumbnail,
      reviews,
      price,
      hotelId: id,
      referralObject,
      replayHotelModel: model,
      displayViewed,
      ratio: imageRatio,
      listingClickHandler: this.listingClickHandler
    };

    return <Title {...titleProps} {...extraProps} />;
  }

  private routeStoreGoBack() {
    this.props.routeStore.goBack();
  }

  public render() {
    const { className, defaultListingOnly, index, loading, model, routeStore } = this.props;
    const {
      alternateDates,
      disclaimerDialog,
      hasAvailableOffer,
      hotelDescription,
      hotelId,
      loyaltyEarnAwards,
      name,
      neighborhood,
      pinnedDetails,
      price,
      primaryCardMessages,
      primaryCardMessagingModel,
      propertyListingBadgeModel,
      propertySummary,
      reviews,
      secondaryCardMessage,
      starRatingIcon,
      stars,
      supportingMessages,
      scarcityMessage,
      thumbnail,
      travelAd,
      vipMessage
    } = model;
    const secondary = propertyListingBadgeModel && propertyListingBadgeModel.secondary;
    const tertiary = propertyListingBadgeModel && propertyListingBadgeModel.tertiary;

    const outerLiClassName = className;

    const listingWrapperProps = {
      className: outerLiClassName,
      'data-stid': this.props['data-stid'] || '',
      ref: this.setItemRef,
      tabIndex: -1
    };

    const alternateDatesProps = {
      handleClick: this.handleAdditionalInfoClick,
      model: alternateDates
    };
    const { loyaltyBaseEarnMessage, loyaltyBaseEarnAccessibilityMessage } = { ...loyaltyEarnAwards };
    const listingMessages = loading ? null : (
      <React.Fragment>
        <ErrorBoundary label="hotel.search.listing.reviews">
          <SearchResultsReviews className="listing__reviews" reviews={reviews} />
        </ErrorBoundary>
        <ErrorBoundary label="hotel.search.listing.messaging">
          <Messaging
            className="listing__messaging uitk-grid uitk-type-300"
            primaryMessaging={primaryCardMessagingModel}
            primaryMessagingList={primaryCardMessages}
          />
        </ErrorBoundary>
        {loyaltyBaseEarnMessage && (
          <ErrorBoundary label="hotel.search.listing.baseEarn">
            <div data-stid="hsr_loyalty_base_earn" className="listing__baseearn uitk-grid uitk-type-300">
              {loyaltyBaseEarnAccessibilityMessage && <span className="is-visually-hidden">{loyaltyBaseEarnAccessibilityMessage}</span>}
              <span
                aria-hidden={Boolean(loyaltyBaseEarnAccessibilityMessage)}
                className="pwa-theme--loyalty"
                dangerouslySetInnerHTML={{ __html: loyaltyBaseEarnMessage }}
              />
            </div>
          </ErrorBoundary>
        )}
      </React.Fragment>
    );

    const DefaultListing = (
      <li {...listingWrapperProps}>
        {loading ? (
          <PlaceholderCard index={index} />
        ) : (
          <UitkCard className="has-action listing__card">
            <ListingName name={name} visuallyHidden={true} />
            <div className="link-container">
              <ListingErrorBoundary thumbnail={thumbnail} name={name}>
                {this.getTitleComponent()}
              </ListingErrorBoundary>
              <UitkCardContent className="all-y-padding-three all-x-padding-three listing-content">
                {vipMessage && <div className="uitk-cell all-cell-1-1 uitk-type-200 uitk-type-medium listing-vip">{vipMessage}</div>}
                <div className="uitk-cell all-cell-fill uitk-type-300">
                  <ListingName name={name} ariaHidden={true} />
                  {stars && (
                    <div className="all-b-padding-half">
                      <span className="is-visually-hidden">
                        <LocalizedText message={'hotels.searchResults.filtersDialog.starRating.label'} />
                      </span>
                      <UitkRating icon={UitkRatingIcon[starRatingIcon]} rating={String(stars)} />
                    </div>
                  )}
                  <HighightedPropertyDetails propertyDetails={propertySummary} />
                  <Neighborhood neighborhood={neighborhood} />
                  {listingMessages}
                </div>
                <PriceAndSupportingMessagesContainer
                  {...{ secondaryCardMessage, supportingMessages, price, scarcityMessage }}
                  wrapPrice={true}
                  viewSmall={true}
                  priceFontWeight="600"
                  hotelId={model.hotelId}
                />
                {disclaimerDialog && (
                  <BreakfastAndWifiDisclaimer
                    disclaimerDialog={disclaimerDialog}
                    handleBack={this.routeStoreGoBack}
                    hotelId={model.hotelId}
                    routeStore={routeStore}
                  />
                )}
                <ListingDescription description={hotelDescription} />
              </UitkCardContent>
              {pinnedDetails && hasAvailableOffer && <Footer />}
              <CardLink {...this.props} index={index} listingClickHandler={this.listingClickHandler} />
            </div>
            {alternateDates && (
              <AdditionalInfo {...ListingComponent.alternateDatesInfo} displayHeaderText={false} keepOpen={false}>
                <AlternateDates {...alternateDatesProps} />
              </AdditionalInfo>
            )}
          </UitkCard>
        )}
      </li>
    );

    if (defaultListingOnly) {
      return DefaultListing;
    }

    const HorizontalListing = imageAspectRatio => (
      <li {...listingWrapperProps}>
        {loading ? (
          <PlaceholderCard index={index} className="horizontal-placeholder" />
        ) : (
          <UitkCard imageLayout={UitkCardImageLayout.LEFT_FULLBLEED}>
            <ListingName name={name} visuallyHidden={true} />
            <div className="link-container uitk-grid">
              {pinnedDetails && pinnedDetails.heading && <TitleHeading headingInfo={pinnedDetails} />}
              <UitkCardMedia>
                <ListingErrorBoundary thumbnail={thumbnail} name={name}>
                  {this.getTitleComponent(imageAspectRatio, {
                    displaySecondaryBadge: false,
                    displayHeading: false
                  })}
                </ListingErrorBoundary>
              </UitkCardMedia>
              <UitkCardContent className="all-y-padding-three all-x-padding-three listing-content">
                <div className="uitk-cell all-cell-fill uitk-type-300">
                  {vipMessage && <div className="uitk-cell uitk-type-200 uitk-type-medium listing-vip">{vipMessage}</div>}
                  <ListingName name={name} ariaHidden={true} />
                  {stars && (
                    <div className="all-b-padding-half">
                      <span className="is-visually-hidden">
                        <LocalizedText message={'hotels.searchResults.filtersDialog.starRating.label'} />
                      </span>
                      <UitkRating icon={UitkRatingIcon[starRatingIcon]} rating={String(stars)} />
                    </div>
                  )}
                  <HighightedPropertyDetails propertyDetails={propertySummary} />
                  <Neighborhood neighborhood={neighborhood} />
                  <ListingTravelAd travelAd={travelAd} />
                </div>
                <PriceAndSupportingMessagesContainer
                  {...{ secondaryCardMessage, supportingMessages, price, secondary, scarcityMessage }}
                  viewSmall={false}
                  wrapPrice={false}
                  priceFontWeight="600"
                  hotelId={model.hotelId}
                />
                <div className="uitk-cell all-cell-align-bottom">
                  {listingMessages}
                  {tertiary &&
                    tertiary.text && (
                      <div className="uitk-cell all-x-gutter-two uitk-type-right all-cell-shrink">
                        <UitkBadge theme={UitkBadgeTheme[tertiary.theme]} text={tertiary.text} />
                      </div>
                    )}
                </div>
                {disclaimerDialog && (
                  <BreakfastAndWifiDisclaimer
                    disclaimerDialog={disclaimerDialog}
                    handleBack={this.routeStoreGoBack}
                    hotelId={hotelId}
                    routeStore={routeStore}
                  />
                )}
                <ListingDescription description={hotelDescription} />
              </UitkCardContent>
              {pinnedDetails &&
                hasAvailableOffer && (
                  <AbacusExperiment name="pwa_hsr_pinned_property">
                    <AbacusControl>
                      <Footer className="uitk-type-right" />
                    </AbacusControl>
                    <AbacusVariant bucket={1}>
                      <Footer className="uitk-type-right" />
                    </AbacusVariant>
                  </AbacusExperiment>
                )}
              <CardLink {...this.props} index={index} listingClickHandler={this.listingClickHandler} />
            </div>
            {alternateDates && (
              <AdditionalInfo {...ListingComponent.alternateDatesInfo} displayHeaderText={true} keepOpen={true}>
                <AlternateDates {...alternateDatesProps} />
              </AdditionalInfo>
            )}
          </UitkCard>
        )}
      </li>
    );

    return (
      <Viewport>
        <ViewSmall>{DefaultListing}</ViewSmall>
        <ViewMedium>{HorizontalListing(UitkFigureAspectRatioType.R3_2)}</ViewMedium>
        <ViewLarge>{HorizontalListing(UitkFigureAspectRatioType.R16_9)}</ViewLarge>
      </Viewport>
    );
  }
}

interface DisclaimerProps {
  disclaimerDialog: DisclaimerViewModel;
  handleBack?: () => void;
  hotelId?: number;
  routeStore?: RouteStore;
}

const BreakfastAndWifiDisclaimer = ({ disclaimerDialog, handleBack, hotelId, routeStore }: DisclaimerProps) => {
  const { disclaimerContent, disclaimerLink } = disclaimerDialog;
  const dialogId = hotelId ? `BreakfastAndWifiDisclaimerDialog-${hotelId}` : 'BreakfastAndWifiDisclaimerDialog';
  const dialogQuery = routeStore.mergeQueryString(routeStore.search, { pwaDialog: dialogId });

  return (
    <div className="uitk-grid all-t-padding-two" data-stid="breakfast-wifi-disclaimer">
      <UitkMoreInfoTrigger>
        <LinkTracked
          linkName="Breakfast and Wifi Disclaimer Dialog"
          rfrr=".BreakfastAndWifi.DisclaimerDialog"
          to={{ pathname: routeStore.pathname, search: dialogQuery }}
        >
          <div className="uitk-cell uitk-type-300 pwa-theme--grey-700">
            <span className="breakfast-and-wifi-disclaimer-link-text">{disclaimerLink}</span>
          </div>
          <UitkIcon name="info_outline" className="pwa-theme--grey-700 all-r-padding-half" />
        </LinkTracked>
      </UitkMoreInfoTrigger>
      {disclaimerContent && (
        <DisclaimerDialogBulletList
          dialogId={dialogId}
          dialogType="pwaDialog"
          disclaimerText={disclaimerContent}
          handleClose={handleBack}
          layerId="breakfast-and-wifi-disclaimer"
          disclaimerTextId="breakfast-and-wifi-disclaimer-dialog-text"
        />
      )}
    </div>
  );
};

export const Listing = inject('searchResults', 'currentHotel', 'context', 'shortlist', 'analytics', 'experiment')(ListingComponent);
